import { Navigate } from "react-router";
import { useIntl } from "react-intl";

import { BillingSection } from "@/components/billing/billing-section";
import { CenteredLoader } from "@/components/common";
import { BrandSettingsShell } from "@/components/settings/brand-settings-shell";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { trpc } from "@/lib/trpc";

export default function BrandBillingPage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const billingAvailabilityQuery = trpc.brand.getBillingAvailability.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const billingOverviewQuery = trpc.brand.getBillingOverview.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id && billingAvailabilityQuery.data?.enabled), staleTime: 0 },
  );
  const billingPlansQuery = trpc.billing.listPlans.useQuery(undefined, {
    enabled: Boolean(brand?.id && billingAvailabilityQuery.data?.enabled),
    staleTime: 0,
  });
  const creditPacksQuery = trpc.billing.listCreditPacks.useQuery(undefined, {
    enabled: Boolean(brand?.id && billingAvailabilityQuery.data?.enabled),
    staleTime: 0,
  });
  const billingInvoicesQuery = trpc.billing.listInvoices.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id && billingAvailabilityQuery.data?.enabled), staleTime: 0 },
  );
  const createCheckoutSession = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
  });
  const changeSubscriptionPlan = trpc.billing.changeSubscriptionPlan.useMutation({
    onSuccess: async () => {
      await billingOverviewQuery.refetch();
    },
  });
  const createCreditPackCheckoutSession = trpc.billing.createCreditPackCheckoutSession.useMutation({
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
  });
  const cancelSubscription = trpc.billing.cancelSubscription.useMutation({
    onSuccess: async () => {
      await billingOverviewQuery.refetch();
    },
  });
  const resumeSubscription = trpc.billing.resumeSubscription.useMutation({
    onSuccess: async () => {
      await billingOverviewQuery.refetch();
    },
  });
  const createPortalSession = trpc.billing.createPortalSession.useMutation({
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
  });

  if (
    brandQuery.isPending ||
    (brand && billingAvailabilityQuery.isPending) ||
    (billingAvailabilityQuery.data?.enabled && billingOverviewQuery.isPending) ||
    (billingAvailabilityQuery.data?.enabled && billingPlansQuery.isPending) ||
    (billingAvailabilityQuery.data?.enabled && creditPacksQuery.isPending)
  ) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "brandSettings.loading",
          defaultMessage: "Loading brand settings...",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  if (!billingAvailabilityQuery.data?.enabled) {
    return <Navigate to={buildBrandPath(brand.id, "/settings")} replace />;
  }

  if (
    billingOverviewQuery.error ||
    billingPlansQuery.error ||
    creditPacksQuery.error ||
    !billingOverviewQuery.data
  ) {
    return (
      <BrandSettingsShell brand={brand} activeSection="billing" billingEnabled>
        <div className="rounded-3xl border border-red-800/20 bg-red-50 p-5 text-sm text-red-950">
          {billingOverviewQuery.error?.message ??
            billingPlansQuery.error?.message ??
            creditPacksQuery.error?.message ??
            intl.formatMessage({
              id: "brandSettings.billing.loadError",
              defaultMessage: "Unable to load billing details.",
            })}
        </div>
      </BrandSettingsShell>
    );
  }

  return (
    <BrandSettingsShell brand={brand} activeSection="billing" billingEnabled>
      <BillingSection
        overview={billingOverviewQuery.data}
        plans={billingPlansQuery.data ?? []}
        creditPacks={creditPacksQuery.data ?? []}
        invoices={billingInvoicesQuery.data ?? []}
        isCheckoutPending={createCheckoutSession.isPending || changeSubscriptionPlan.isPending}
        isCreditPackCheckoutPending={createCreditPackCheckoutSession.isPending}
        isPortalPending={createPortalSession.isPending}
        isCancelPending={cancelSubscription.isPending}
        isResumePending={resumeSubscription.isPending}
        isInvoicesPending={billingInvoicesQuery.isPending}
        billingActionError={
          createCheckoutSession.error?.message ??
          changeSubscriptionPlan.error?.message ??
          createCreditPackCheckoutSession.error?.message ??
          cancelSubscription.error?.message ??
          resumeSubscription.error?.message ??
          billingInvoicesQuery.error?.message ??
          createPortalSession.error?.message ??
          null
        }
        onCheckout={(input) => createCheckoutSession.mutate({ brandId: brand.id, ...input })}
        onChangePlan={(input) => changeSubscriptionPlan.mutate({ brandId: brand.id, ...input })}
        onCreditPackCheckout={(creditPackId) =>
          createCreditPackCheckoutSession.mutate({ brandId: brand.id, creditPackId })
        }
        onCancelSubscription={() => cancelSubscription.mutate({ brandId: brand.id })}
        onResumeSubscription={() => resumeSubscription.mutate({ brandId: brand.id })}
        onManageBilling={() => createPortalSession.mutate({ brandId: brand.id })}
      />
    </BrandSettingsShell>
  );
}
